import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

interface ForgotPasswordProps {
    open: boolean;
    onClose: () => void;
    onContinue?: (email: string) => Promise<void>;
}

export default function ForgotPassword({ open, onClose, onContinue }: ForgotPasswordProps) {
    const { t } = useTranslation();
    const [email, setEmail] = React.useState('');
    const [emailError, setEmailError] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (onContinue) {
            setSubmitting(true);
            const email = (event.currentTarget.elements.namedItem('email') as HTMLInputElement).value;
            onContinue(email).finally(() => {
                setSubmitting(false);
                onClose();
            });
        }
    };

    const validateInputs = () => {
        const email = document.getElementById('email') as HTMLInputElement;

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            isValid = false;
        } else {
            setEmailError(false);
        }

        return isValid;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                component: 'form',
                onSubmit: handleSubmit,
                sx: { backgroundImage: 'none' },
            }}
        >
            <DialogTitle>{t('welcome.resetPassword.title')}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                <DialogContentText>{t('welcome.resetPassword.message')}</DialogContentText>
                <OutlinedInput
                    error={emailError}
                    autoFocus
                    required
                    margin='dense'
                    id='email'
                    name='email'
                    placeholder='your@email.com'
                    type='email'
                    fullWidth
                    value={email}
                    color={emailError ? 'error' : 'primary'}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setEmail(event.target.value);
                    }}
                    autoComplete='email'
                />
            </DialogContent>
            <DialogActions sx={{ pb: 3, px: 3 }}>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button loading={submitting} variant='contained' type='submit' onClick={validateInputs} loadingIndicator={<CircularProgress size={16} />}>
                    {t('common.continue')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
